export const MAX_REQUESTED_PATH_LENGTH = 160;

export type UkRequestedPathCategory = "local_lessons" | "ely" | "bury_st_edmunds" | "local_faq" | "unknown";
export type ViewportClass = "mobile" | "tablet" | "desktop";

export type NotFoundRecoveryEvent = {
  action: "view" | "recovery_click";
  requested_path_category: UkRequestedPathCategory;
  site: "uk";
  destination_path: string;
  referrer_domain: string;
  viewport_class: ViewportClass;
};

export type NotFoundRecoveryEventCallback = (event: NotFoundRecoveryEvent) => void;

export type UkRecoveryDestination = {
  category: UkRequestedPathCategory;
  href: string;
  label: string;
};

const SENSITIVE_KEY = /^(?:access[-_]?token|auth|authorization|code|email|key|otp|password|recovery[-_]?token|secret|session|session[-_]?id|token|uid|user[-_]?id)$/i;
const EMAIL_VALUE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const UUID_VALUE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TOKEN_VALUE = /^(?:[A-Za-z0-9_-]{32,}|(?:cs|pi|seti|ch|cus|sub|in|si|pm|evt)_[A-Za-z0-9_]{12,})$/i;
const SAFE_SEGMENT_CHARACTERS = /[^A-Za-z0-9!$&'()*+,;=:@._~%-]+/g;

function decodeSegment(segment: string) {
  let decoded = segment;
  for (let depth = 0; depth < 2 && /%[0-9a-f]{2}/i.test(decoded); depth += 1) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return null;
    }
  }
  if (/%[0-9a-f]{2}/i.test(decoded)) return null;
  return decoded;
}

function sanitizeSegment(segment: string, redactBecausePreviousSegmentIsSensitive: boolean) {
  if (!segment) return "";
  const decoded = decodeSegment(segment);
  if (decoded === null || redactBecausePreviousSegmentIsSensitive) return "redacted";

  const normalized = decoded.normalize("NFKC").trim();
  if (
    EMAIL_VALUE.test(normalized) ||
    UUID_VALUE.test(normalized) ||
    TOKEN_VALUE.test(normalized) ||
    normalized.length > 48 ||
    /^\d{13,}$/.test(normalized)
  ) {
    return "redacted";
  }

  return normalized
    .replace(SAFE_SEGMENT_CHARACTERS, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

/**
 * Keeps a bounded, pathname-only recovery representation. It is never rendered,
 * used as a redirect destination, or sent to analytics.
 */
export function sanitizeRequestedPath(rawPath: string | null | undefined) {
  if (!rawPath) return "/";

  const pathnameOnly = rawPath.split(/[?#]/, 1)[0] ?? "/";
  const rawSegments = pathnameOnly.split("/").filter(Boolean);
  const safeSegments: string[] = [];
  let previousSegmentIsSensitive = false;

  for (const rawSegment of rawSegments) {
    const segment = sanitizeSegment(rawSegment, previousSegmentIsSensitive);
    previousSegmentIsSensitive = SENSITIVE_KEY.test(decodeSegment(rawSegment) ?? "");
    if (!segment) continue;

    const candidate = `/${[...safeSegments, segment].join("/")}`;
    if (candidate.length > MAX_REQUESTED_PATH_LENGTH) {
      safeSegments.push("truncated");
      break;
    }
    safeSegments.push(segment);
  }

  return `/${safeSegments.join("/")}`.slice(0, MAX_REQUESTED_PATH_LENGTH) || "/";
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function resolveUkRecovery(rawPath: string | null | undefined): UkRecoveryDestination {
  const pathname = sanitizeRequestedPath(rawPath).toLowerCase();

  if (matchesPrefix(pathname, "/singing-lessons-ely")) {
    return { category: "ely", href: "/singing-lessons-ely", label: "View Ely Singing Lessons" };
  }
  if (matchesPrefix(pathname, "/singing-lessons-bury-st-edmunds")) {
    return {
      category: "bury_st_edmunds",
      href: "/singing-lessons-bury-st-edmunds",
      label: "View Bury St Edmunds Singing Lessons",
    };
  }
  if (matchesPrefix(pathname, "/faq") || matchesPrefix(pathname, "/local-lessons/faq")) {
    return { category: "local_faq", href: "/local-lessons#faq", label: "Read Local Lesson FAQs" };
  }
  if (matchesPrefix(pathname, "/local-lessons")) {
    return { category: "local_lessons", href: "/local-lessons", label: "Return to Local Lessons" };
  }

  return { category: "unknown", href: "/local-lessons", label: "View Local Lesson Information" };
}

export function getViewportClass(width: number): ViewportClass {
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function sanitizeReferrerDomain(referrer: string | null | undefined) {
  if (!referrer) return "direct";
  try {
    const url = new URL(referrer);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "direct";
    const hostname = url.hostname.toLowerCase().replace(/[^a-z0-9.-]/g, "").slice(0, 100);
    return hostname || "direct";
  } catch {
    return "direct";
  }
}

const ALLOWED_EVENT_DESTINATIONS = new Set([
  "/contact",
  "/local-lessons",
  "/singing-lessons-bury-st-edmunds",
  "/singing-lessons-ely",
]);

function sanitizeDestinationPath(destination: string) {
  const pathname = destination.split(/[?#]/, 1)[0] || "/";
  return ALLOWED_EVENT_DESTINATIONS.has(pathname) ? pathname : "/local-lessons";
}

export function buildUkNotFoundRecoveryEvent({
  action,
  rawPath,
  destination,
  referrer,
  viewportWidth,
}: {
  action: NotFoundRecoveryEvent["action"];
  rawPath: string | null | undefined;
  destination: string;
  referrer: string | null | undefined;
  viewportWidth: number;
}): NotFoundRecoveryEvent {
  return {
    action,
    requested_path_category: resolveUkRecovery(rawPath).category,
    site: "uk",
    destination_path: sanitizeDestinationPath(destination),
    referrer_domain: sanitizeReferrerDomain(referrer),
    viewport_class: getViewportClass(viewportWidth),
  };
}
